
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Custom hook for managing debug functionality across the application
 * Handles debug mode toggle, debug output, and debug operations
 */
export const useDebugMode = () => {
  const [debugClicks, setDebugClicks] = useState(0);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugOutput, setDebugOutput] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Toggles debug mode after 5 clicks
   */
  const toggleDebugMode = () => {
    setDebugClicks(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        setShowDebugInfo(true);
        return 0;
      }
      return newCount;
    });
  };

  /**
   * Checks employees table in Supabase database
   */
  const checkEmployees = async () => {
    setIsLoading(true);
    setDebugOutput("Checking employees table...");
    
    try {
      const { data, error } = await supabase.from('medewerkers').select('*');
      
      if (error) {
        setDebugOutput(`Error fetching employees: ${error.message}`);
        return;
      }
      
      if (!data || data.length === 0) {
        setDebugOutput("No employees found in database");
        return;
      }
      
      const userList = data.map(user => ({
        username: user.username,
        naam: user.naam,
        functie: user.functie
      }));
      
      setDebugOutput(`Found ${data.length} employees:\n${JSON.stringify(userList, null, 2)}`);
    } catch (e) {
      setDebugOutput(`Exception: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resets debug mode
   */
  const resetDebugMode = () => {
    setDebugClicks(0);
    setShowDebugInfo(false);
    setDebugOutput("");
  };

  return {
    debugClicks,
    showDebugInfo,
    debugOutput,
    isLoading,
    toggleDebugMode,
    setShowDebugInfo,
    setDebugOutput,
    checkEmployees,
    resetDebugMode
  };
};
